/**
 * Cross-app navigation utility for micro-frontend applications
 * Handles navigation between different child applications with proper base path handling
 */
/**
 * Navigate to a route in a different child application
 * @param targetPath - The full path including the app name (e.g., '/forecasting/select-mrd?flow=newDataset')
 * @param availableProducts - Optional array of available product names for validation
 */
export var navigateToChildApp = function navigateToChildApp(targetPath, availableProducts) {
    // Ensure the path starts with a forward slash
    var normalizedPath = targetPath.startsWith('/') ? targetPath : "/".concat(targetPath);
    // Extract the app name from the path
    var pathParts = normalizedPath.split('/').filter(Boolean);
    var appName = pathParts.find(Boolean) || '';
    // Validate the target app exists if products list is provided
    if (availableProducts && !availableProducts.includes(appName)) {
        console.error("Target app '".concat(appName, "' is not configured in available products"));
        return;
    }
    // Use React Router navigation instead of full reload
    // This requires the router to be mounted at the correct basename for each app
    // Use window.history.pushState and dispatch popstate for navigation
    globalThis.window.history.pushState(null, '', normalizedPath);
    globalThis.window.dispatchEvent(new PopStateEvent('popstate'));
};
/**
 * Navigate to a route within the current application
 * @param path - The path within the current app
 */
export var navigateWithinApp = function navigateWithinApp(path) {
    // For same-app navigation, we can use the router
    var normalizedPath = path.startsWith('/') ? path : "/".concat(path);
    globalThis.window.history.pushState(null, '', normalizedPath);
    // Dispatch a popstate event to trigger React Router updates
    globalThis.window.dispatchEvent(new PopStateEvent('popstate'));
};
/**
 * Get the current app name from the URL
 * @returns The current app name or null if on the home page
 */
export var getCurrentAppName = function getCurrentAppName() {
    var pathParts = globalThis.window.location.pathname.split('/').filter(Boolean);
    return pathParts.length > 0 ? pathParts[0] : null;
};
/**
 * Check if a path belongs to a different app than the current one
 * @param targetPath - The path to check
 * @returns True if the path is for a different app
 */
export var isDifferentApp = function isDifferentApp(targetPath) {
    var normalizedPath = targetPath.startsWith('/') ? targetPath : "/".concat(targetPath);
    var pathParts = normalizedPath.split('/').filter(Boolean);
    var targetAppName = pathParts.length > 0 ? pathParts[0] : null;
    var currentAppName = getCurrentAppName();
    return targetAppName !== currentAppName;
};
/**
 * Smart navigation function that determines whether to use cross-app or within-app navigation
 * @param targetPath - The target path
 * @param availableProducts - Optional array of available product names for validation
 */
export var smartNavigate = function smartNavigate(targetPath, availableProducts) {
    if (isDifferentApp(targetPath)) {
        navigateToChildApp(targetPath, availableProducts);
    }
    else {
        navigateWithinApp(targetPath);
    }
};
/**
 * Build a URL for cross-app navigation with query parameters
 * @param appName - The target app name
 * @param route - The route within the app
 * @param queryParams - Query parameters as an object
 * @returns The complete path string
 */
export var buildCrossAppUrl = function buildCrossAppUrl(appName, route, queryParams) {
    var normalizedRoute = route.startsWith('/') ? route : "/".concat(route);
    var basePath = "/".concat(appName).concat(normalizedRoute);
    if (queryParams && Object.keys(queryParams).length > 0) {
        var searchParams = new URLSearchParams(queryParams);
        return "".concat(basePath, "?").concat(searchParams.toString());
    }
    return basePath;
};
export var navigateToApp = function navigateToApp(appPath) {
    // Use React Router navigation instead of full reload
    globalThis.window.history.pushState(null, '', appPath);
    globalThis.window.dispatchEvent(new PopStateEvent('popstate'));
};
